import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FAQ section with Schema.org FAQPage JSON-LD markup for SEO / AI search.
 * Renders an accordion of published FAQs.
 */
export const FaqSection = ({ heading = 'Frequently Asked Questions', subheading = 'Everything you need to know before you cook, list, or buy.' }) => {
  const [faqs, setFaqs] = useState([]);
  const [openId, setOpenId] = useState(null);
  const scriptRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/faqs`).then(({ data }) => setFaqs(data)).catch(() => setFaqs([]));
  }, []);

  // Inject JSON-LD once faqs are loaded (removed on unmount to avoid duplication across route changes)
  useEffect(() => {
    if (faqs.length === 0) return;
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer }
      }))
    };
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.text = JSON.stringify(jsonLd);
    s.setAttribute('data-faq-schema', 'true');
    document.head.appendChild(s);
    scriptRef.current = s;
    return () => {
      if (scriptRef.current && scriptRef.current.parentNode) {
        scriptRef.current.parentNode.removeChild(scriptRef.current);
      }
    };
  }, [faqs]);

  if (faqs.length === 0) return null;

  return (
    <section
      className="w-full py-16 md:py-24"
      style={{ backgroundColor: '#F9F6F0' }}
      data-testid="faq-section"
      aria-labelledby="faq-heading"
    >
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="mb-10 md:mb-14">
          <p
            className="uppercase mb-3"
            style={{ fontFamily: 'Manrope', fontSize: '12px', fontWeight: 600, letterSpacing: '0.18em', color: '#D95A41' }}
          >
            FAQs
          </p>
          <h2
            id="faq-heading"
            style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
            className="text-4xl md:text-5xl leading-tight mb-3"
          >
            {heading}
          </h2>
          <p style={{ fontFamily: 'Manrope', color: '#5C5C5C' }} className="text-base md:text-lg">
            {subheading}
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((f) => {
            const isOpen = openId === f.id;
            return (
              <div
                key={f.id}
                className="rounded-md border border-[#E5E0D8] overflow-hidden transition-colors"
                style={{ backgroundColor: '#FFFFFF' }}
                data-testid={`faq-item-${f.id}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : f.id)}
                  className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 md:px-6 md:py-5 hover:bg-[#F9F6F0]/60 transition-colors"
                  aria-expanded={isOpen}
                  data-testid={`faq-toggle-${f.id}`}
                >
                  <span
                    style={{ fontFamily: 'Manrope', fontWeight: 600, color: '#1A1A1A' }}
                    className="text-base md:text-lg"
                  >
                    {f.question}
                  </span>
                  <ChevronDown
                    size={20}
                    strokeWidth={1.5}
                    style={{
                      color: '#D95A41',
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.25s ease'
                    }}
                    className="flex-shrink-0"
                  />
                </button>
                <div
                  className="overflow-hidden"
                  style={{
                    maxHeight: isOpen ? '600px' : '0px',
                    transition: 'max-height 0.3s ease-in-out'
                  }}
                >
                  <div
                    className="px-5 pb-5 md:px-6 md:pb-6"
                    style={{ fontFamily: 'Manrope', color: '#5C5C5C', lineHeight: 1.65 }}
                    data-testid={`faq-answer-${f.id}`}
                  >
                    {f.answer}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
